import { getEnv } from '@unni/platform'
import { useEffect, useState } from 'react'
import { createFetchWebEventCollector, EventName, PageName } from 'web-event-collector'
import { DoctorPageViewedEvent } from 'web-event-collector/src/interfaces/event/DoctorPageViewed'

import { OptionalAgreeType } from '@/components/atomic/organisms/NormalConsultationBottomSheet'
import { HospitalModuleProps, SquareReviewElementProps } from '@/features/doctors/[doctorId]/index/helpers/types'
import { DoctorDetailTemplate } from '@/features/doctors/[doctorId]/index/components/doctor-detail-template/doctor-detail-template'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { createCommonEvent } from '@/utils/eventCollector'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { useLocales } from '@/shared/hooks/useLocales'
import { CommunityOfDoctor, DoctorInfo } from '@/features/doctors/[doctorId]/helpers/types/doctor'

interface Props {
  userId: number
  userCountry: string | null
  userProfileImage: string | null
  doctorInfo: DoctorInfo
  reviews: SquareReviewElementProps[]
  hospitalModuleProps: HospitalModuleProps
  community: CommunityOfDoctor | null
  optionalMarketingItems: (keyof OptionalAgreeType)[]
}

export const DoctorsDoctorId = ({
  userId,
  userCountry,
  userProfileImage,
  doctorInfo,
  reviews,
  hospitalModuleProps,
  community,
  optionalMarketingItems,
}: Props) => {
  const [isTabActive, setIsTabActive] = useState(true)
  const { countryPathnamePrefix } = useLocales()

  useEffect(() => {
    function handleVisibilityChange() {
      setIsTabActive(document.visibilityState === 'visible')
    }

    const isVisibilityApiSupported = typeof document.hidden !== 'undefined'

    if (isVisibilityApiSupported) {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    return () => {
      if (isVisibilityApiSupported) {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [])

  const [isDoctorProfileImageLoaded, setIsDoctorProfileImageLoaded] = useState(false)

  useEffect(() => {
    if (!(!!userId && isDoctorProfileImageLoaded && isTabActive)) {
      return
    }

    const timeoutId = setTimeout(() => {
      const { emitDoctorPageViewed } = createFetchWebEventCollector({
        baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
      })

      const predecessor = localStorage.getItem('predecessor')

      localStorage.removeItem('predecessor')

      const commonEvent = createCommonEvent(navigator.userAgent, userId)

      const doctorPageViewedEvent: DoctorPageViewedEvent = {
        ...commonEvent,
        Name: EventName.DoctorPageViewed,
        Body: {
          ...commonEvent.Body,
          doctorId: doctorInfo.id,
          ...(predecessor && { predecessor: JSON.parse(predecessor) }),
          hospitalId: doctorInfo.hospitalId,
          pageName: PageName.DoctorDetailWeb,
        },
      }

      emitDoctorPageViewed(doctorPageViewedEvent)
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isDoctorProfileImageLoaded, isTabActive, userId])

  useEffect(() => {
    if (userId !== 0) {
      sessionStorage.setItem('user_id', userId.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [userId])

  useEffect(() => {
    sendAirbridgeEvent(AirbridgeEvents.DOCTOR_PAGE_VIEW, {
      semanticAttributes: {
        products: [
          {
            productID: doctorInfo.id,
            name: doctorInfo.name,
            brandID: doctorInfo.hospitalId,
            brandName: doctorInfo.hospitalName,
          },
        ],
      },
    })
  }, [doctorInfo])

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={userProfileImage || undefined} bottomPaddingToAdd="108px">
        <DoctorDetailTemplate
          userId={userId}
          userCountry={userCountry || undefined}
          doctorInfo={doctorInfo}
          pathPrefix={countryPathnamePrefix}
          reviews={reviews}
          hospital={hospitalModuleProps}
          community={community || undefined}
          optionalMarketingItems={optionalMarketingItems}
          onClickConsultation={() => {
            sendAirbridgeEvent(AirbridgeEvents.CONVERSION_BUTTON_CLICK, {
              semanticAttributes: {
                type: 'DOCTOR',
                products: [
                  {
                    productID: '',
                    name: doctorInfo.name,
                    categoryID: '',
                    categoryName: '',
                    brandID: doctorInfo.hospitalId,
                    brandName: doctorInfo.hospitalName,
                    price: 0,
                    currency: hospitalModuleProps.country === 'KR' ? 'KRW' : 'JPY',
                  },
                ],
              },
            })
          }}
          onProfileImageLoaded={() => {
            setIsDoctorProfileImageLoaded(true)
          }}
        />
      </TopDownloadScreen>
    </ResponsiveLayout>
  )
}
